// redux/automations/slice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AutomationState } from "./types";
import { DEFAULT_COMPANY } from "./constants";
import {
  fetchCompanies,
  fetchAllNodeConfigs,
  fetchNodeDynamicData,
  validateNode,
  saveWorkflow,
  fetchAllWorkflows,
  fetchWorkflow,
  deleteWorkflow,
  toggleWorkflowStatus,
  updateWorkflowSettings,
  fetchExecutionHistory,
  fetchExecutionDetails,
  fetchVersionHistory,
  validateWorkflow,
} from "./thunks";

const initialState: AutomationState = {
  // Company state
  companies: [],
  activeCompany: DEFAULT_COMPANY,
  loading: false,

  // Node configuration state
  nodeConfigs: {
    triggers: [],
    conditions: [],
    actions: [],
  },
  nodeDynamicDataMap: {},
  validationErrors: {},

  // Workflow state
  workflows: [],
  currentWorkflow: null,
  workflowSettings: null,
  workflowPagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },

  // Execution state
  executionHistory: [],
  currentExecution: null,
  executing: false,

  // Version history state
  versionHistory: [],

  // Validation state
  workflowValidation: null,
};

const automationSlice = createSlice({
  name: "automations",
  initialState,
  reducers: {
    // Company actions
    setActiveCompany: (state, action: PayloadAction<any>) => {
      state.activeCompany = action.payload;
    },
    resetActiveCompany: (state) => {
      state.activeCompany = DEFAULT_COMPANY;
    },

    // Workflow actions
    setCurrentWorkflow: (state, action: PayloadAction<any>) => {
      state.currentWorkflow = action.payload;
    },
    clearCurrentWorkflow: (state) => {
      state.currentWorkflow = null;
      state.workflowSettings = null;
    },

    // Validation actions
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },
    clearWorkflowValidation: (state) => {
      state.workflowValidation = null;
    },

    // Node data actions
    clearNodeDynamicDataMap: (state) => {
      state.nodeDynamicDataMap = {};
    },

    // Execution actions
    clearExecutionHistory: (state) => {
      state.executionHistory = [];
    },
    clearCurrentExecution: (state) => {
      state.currentExecution = null;
    },

    // Reset actions
    resetWorkspaceState: (state) => {
      state.nodeDynamicDataMap = {};
      state.validationErrors = {};
      state.currentWorkflow = null;
      state.workflowSettings = null;
      state.executionHistory = [];
      state.currentExecution = null;
      state.workflowValidation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch companies
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload;
        if (!state.activeCompany?.company_id && action.payload.length > 0) {
          const first = action.payload[0];
          state.activeCompany = {
            company_id: first.id,
            company_name: first.name,
            company_type: first.type,
          };
        }
      })
      .addCase(fetchCompanies.rejected, (state) => {
        state.loading = false;
      })

      // Fetch all node configs
      .addCase(fetchAllNodeConfigs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllNodeConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.nodeConfigs = action.payload;
      })
      .addCase(fetchAllNodeConfigs.rejected, (state) => {
        state.loading = false;
      })

      // Fetch node dynamic data
      .addCase(fetchNodeDynamicData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNodeDynamicData.fulfilled, (state, action) => {
        state.loading = false;
        const { nodeKey, data } = action.payload;
        state.nodeDynamicDataMap[nodeKey] = data;
      })
      .addCase(fetchNodeDynamicData.rejected, (state) => {
        state.loading = false;
      })

      // Validate node
      .addCase(validateNode.fulfilled, (state, action) => {
        if (!action.payload.valid) {
          state.validationErrors = action.payload.errors || {};
        } else {
          state.validationErrors = {};
        }
      })
      .addCase(validateNode.rejected, (state, action) => {
        state.validationErrors = { error: action.payload };
      })

      // Save workflow
      .addCase(saveWorkflow.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkflow = action.payload.data.workflow;
      })
      .addCase(saveWorkflow.rejected, (state) => {
        state.loading = false;
      })

      // Fetch all workflows
      .addCase(fetchAllWorkflows.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllWorkflows.fulfilled, (state, action) => {
        state.loading = false;
        state.workflows = action.payload.data.workflows;
        state.workflowPagination = action.payload.data.pagination;
      })
      .addCase(fetchAllWorkflows.rejected, (state) => {
        state.loading = false;
      })

      // Fetch single workflow
      .addCase(fetchWorkflow.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkflow = action.payload.data.workflow;
        state.workflowSettings = action.payload.data.settings;
      })
      .addCase(fetchWorkflow.rejected, (state) => {
        state.loading = false;
      })

      // Delete workflow
      .addCase(deleteWorkflow.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.workflows = state.workflows.filter(
          (w) => w.id !== action.payload
        );
        if (state.currentWorkflow?.id === action.payload) {
          state.currentWorkflow = null;
          state.workflowSettings = null;
        }
      })
      .addCase(deleteWorkflow.rejected, (state) => {
        state.loading = false;
      })

      // Toggle workflow status
      .addCase(toggleWorkflowStatus.fulfilled, (state, action) => {
        const { workflowId, isActive } = action.payload;
        const workflow = state.workflows.find((w) => w.id === workflowId);
        if (workflow) {
          workflow.is_active = isActive;
        }
        if (state.currentWorkflow?.id === workflowId) {
          state.currentWorkflow.is_active = isActive;
        }
      })

      // Update workflow settings
      .addCase(updateWorkflowSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateWorkflowSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.workflowSettings = action.payload.data;
      })
      .addCase(updateWorkflowSettings.rejected, (state) => {
        state.loading = false;
      })

      // Fetch execution history
      .addCase(fetchExecutionHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExecutionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.executionHistory = action.payload.data;
      })
      .addCase(fetchExecutionHistory.rejected, (state) => {
        state.loading = false;
      })

      // Fetch execution details
      .addCase(fetchExecutionDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExecutionDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentExecution = action.payload.data;
      })
      .addCase(fetchExecutionDetails.rejected, (state) => {
        state.loading = false;
      })

      // Fetch version history
      .addCase(fetchVersionHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVersionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.versionHistory = action.payload.data;
      })
      .addCase(fetchVersionHistory.rejected, (state) => {
        state.loading = false;
      })

      // Validate workflow
      .addCase(validateWorkflow.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.workflowValidation = action.payload.data;
      })
      .addCase(validateWorkflow.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setActiveCompany,
  resetActiveCompany,
  setCurrentWorkflow,
  clearCurrentWorkflow,
  clearValidationErrors,
  clearWorkflowValidation,
  clearNodeDynamicDataMap,
  clearExecutionHistory,
  clearCurrentExecution,
  resetWorkspaceState,
} = automationSlice.actions;

export default automationSlice.reducer;
